<template>
    <MuvalModal
        v-model="showNotEnabled"
        header-height-class="h-10"
    >
        <template #header>
            <div class="flex items-center gap-3 text-lg font-medium leading-5 text-muval-gray-1">
                <SvgIcon
                    name="movepro_logo"
                    class="block h-5 w-32"
                />
            </div>
        </template>
        <div class="p-4 font-body text-sm leading-6 text-muval-gray-2">
            <div class="space-y-3">
                <p class="text-sm leading-6 text-muval-gray-2">
                    <span class="font-medium text-muval-gray-1">Movepro</span>
                    is a modern, all-in-one job management platform for removalists.
                </p>
                <ul class="list-disc space-y-1 pl-5 text-sm leading-6 text-muval-gray-2 marker:text-muval-gray-2">
                    <li class="text-sm leading-6 text-muval-gray-2">
                        Manage jobs, schedules, staff, vehicles and documents in one place
                    </li>
                    <li class="text-sm leading-6 text-muval-gray-2">
                        Track jobs in real time with field-ready mobile tools
                    </li>
                    <li class="text-sm leading-6 text-muval-gray-2">
                        Reduce admin, improve visibility, and get paid faster
                    </li>
                </ul>
                <p class="text-sm leading-6 text-muval-gray-2">
                    Movepro integrates directly with Muval leads and workflows to help you run jobs more efficiently
                    from booking to completion.
                </p>
                <p class="mb-0">
                    <span class="font-medium text-muval-gray-1">Schedule a demo today to learn more:</span>
                    <a
                        href="tel:1300030455"
                        class="font-medium text-brand hover:underline"
                    >
                        1300 030 455
                    </a>
                </p>
            </div>
        </div>
    </MuvalModal>

    <MuvalModal
        v-model="showCopy"
        modal-size="md:w-2/3"
        header-height-class="h-10"
    >
        <template #header>
            <div class="flex items-center gap-3 text-lg font-medium leading-5 text-muval-gray-1">
                <SvgIcon
                    name="movepro_logo"
                    class="block h-5 w-32"
                />
            </div>
        </template>
        <div class="p-4">
            <div class="mb-3">
                <div class="text-sm text-muval-gray-2">
                    <div class="font-medium text-muval-gray-1">How to import into Movepro</div>
                    <ol class="mt-2 list-decimal space-y-1 pl-5 text-xs text-muval-gray-2">
                        <li>
                            Add Muval as an <span class="font-medium text-muval-gray-1">Affiliate</span> if you haven't
                            already
                        </li>
                        <li>
                            For prefill source, choose
                            <span class="font-medium text-muval-gray-1">Paste information</span>
                        </li>
                        <li>For referrer, select <span class="font-medium text-muval-gray-1">Muval</span></li>
                        <li v-if="moveType">
                            For move type, select <span class="font-medium text-muval-gray-1">{{ moveType }}</span>
                        </li>
                        <li v-else>For move type, select the appropriate option</li>
                        <li>
                            Turn inventory prefill
                            <span class="font-medium text-muval-gray-1">{{ inventoryPrefill }}</span>
                        </li>
                        <li v-if="inventoryPrefill === 'ON'">Select "Per row"</li>
                    </ol>
                </div>
            </div>

            <div class="relative">
                <div class="absolute right-2 top-2 z-10">
                    <MuvalButton
                        quaternary
                        type="button"
                        class="!h-input-small !px-3"
                        @click="copy"
                    >
                        <span class="inline-flex items-center gap-2">
                            <!-- Match the "copy merge tag" icon used in comms templates -->
                            <svg
                                width="11"
                                height="12"
                                viewBox="0 0 11 12"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                class="my-auto text-muval-gray-4"
                            >
                                <path
                                    d="M7.75 10.667H1.91667V3.08366C1.91667 2.76283 1.65417 2.50033 1.33333 2.50033C1.0125 2.50033 0.75 2.76283 0.75 3.08366V10.667C0.75 11.3087 1.275 11.8337 1.91667 11.8337H7.75C8.07083 11.8337 8.33333 11.5712 8.33333 11.2503C8.33333 10.9295 8.07083 10.667 7.75 10.667ZM10.6667 8.33366V1.33366C10.6667 0.691992 10.1417 0.166992 9.5 0.166992H4.25C3.60833 0.166992 3.08333 0.691992 3.08333 1.33366V8.33366C3.08333 8.97533 3.60833 9.50033 4.25 9.50033H9.5C10.1417 9.50033 10.6667 8.97533 10.6667 8.33366ZM9.5 8.33366H4.25V1.33366H9.5V8.33366Z"
                                    fill="currentColor"
                                />
                            </svg>
                            <span>Copy</span>
                        </span>
                    </MuvalButton>
                </div>

                <textarea
                    readonly
                    class="min-h-[22rem] w-full rounded-md border border-muval-gray-5 bg-white p-3 pt-10 font-mono text-xs text-muval-gray-1 focus:outline-none"
                    :value="content"
                    @focus="$event.target.select()"
                />
            </div>
        </div>
    </MuvalModal>
</template>

<script setup>
import MuvalModal from '@components/modal/MuvalModal.vue';
import MuvalButton from '@components/button/MuvalButton.vue';
import { buildMoveproContent } from '@utilities/movepro';

const props = defineProps({
    enabled: {
        type: Boolean,
        default: false,
    },
    dayjs: {
        type: Function,
        required: true,
    },
});

const showNotEnabled = ref(false);
const showCopy = ref(false);
const activeLead = ref(null);

const inventoryPrefill = computed(() => {
    const option = activeLead.value?.move?.currentInventory?.inventoryOption;
    const normalized = option ? String(option).toUpperCase() : null;
    const hasList =
        Array.isArray(activeLead.value?.move?.currentInventory?.items) &&
        activeLead.value.move.currentInventory.items.length > 0;
    const hasInventoryArray =
        Array.isArray(activeLead.value?.move?.inventory) && activeLead.value.move.inventory.length > 0;
    const enabled =
        hasList || hasInventoryArray || (normalized && normalized !== 'NONE' && normalized !== 'NO_INVENTORY');
    return enabled ? 'ON' : 'OFF';
});

const moveType = computed(() => {
    const raw = activeLead.value?.move?.type ?? activeLead.value?.move_type ?? null;
    if (!raw) return null;
    const up = String(raw).toUpperCase();
    if (up === 'LOCAL' || up === 'INTERSTATE') return up;
    return null;
});

const content = computed(() => buildMoveproContent({ lead: activeLead.value, dayjs: props.dayjs }));

function normalizeLeadForMovepro(input) {
    if (!input) return null;

    // In this CRM, lead details often come back as a "lead" with an `order.move`.
    // The Movepro formatter expects `lead.move` at the top level.
    const out = { ...input };
    if (!out.move && out.order?.move) out.move = out.order.move;
    if (!out.customer && out.order?.customer) out.customer = out.order.customer;
    return out;
}

function open(lead) {
    if (!props.enabled) {
        showNotEnabled.value = true;
        return;
    }
    activeLead.value = normalizeLeadForMovepro(lead);
    showCopy.value = true;
}

async function copy() {
    try {
        await navigator.clipboard.writeText(content.value);
    } catch {
        const el = document.createElement('textarea');
        el.value = content.value;
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
    }
}

defineExpose({ open });
</script>
